import { Database, Model } from '@nozbe/watermelondb';
import { v4 as uuidv4 } from 'uuid';

import Location from '../../Location';
import { DBServiceOptionsWithImagesAndValidation } from '../../../../types/dbService';
import { CacheService } from '../../../../types/cacheService';
import {
    EVENT_CONTACT_STATUS,
    EventContactModel,
} from '../../../../types/EventContact';
import { EventModel } from '../../../../types/Events';
import { LocationModel, LocationPayload } from '../../../../types/Location';
import { FirestoreService } from '../../../../types/FirestoreService';
import { NotificationsService } from '../../../../types/notificationsService';
import { mapRawRecord, mapRawRecords, mapRecords } from '../common/mapRecords';

const addRelatedDataBatch = async (data: Model[], database: Database) => {
    await database.batch(...data);
};

const prepareEventLocation = async ({
    location,
    userId,
    serviceOptions,
}: {
    location?: LocationPayload | null;
    userId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    const locationService = new Location(serviceOptions);

    const locationData: {
        preparedLocation: LocationModel | null;
        existingLocation: LocationModel | null;
    } = {
        preparedLocation: null,
        existingLocation: null,
    };

    if (location) {
        const result = await locationService.prepareAddUnique(location, userId);

        if (result) {
            locationData.preparedLocation = result.preparedLocation ?? null;
            locationData.existingLocation = result.existingLocation ?? null;
        }
    }

    return locationData;
};

const clearEventNotificationCache = async ({
    eventId,
    cacheService,
    notificationsService,
}: {
    eventId: string;
    cacheService: CacheService;
    notificationsService: NotificationsService;
}) => {
    const cacheKey = `notification_${eventId}`;
    const cachedNotification = await cacheService.get<string>(cacheKey);

    if (cachedNotification) {
        await cacheService.clear(cacheKey);

        notificationsService.cancelNotification({
            id: cachedNotification,
        });
    }
};

const schedulePN = async ({
    createdEvent,
    userReminder,
    notificationsService,
}: {
    createdEvent: EventModel;
    userReminder?: string | null;
    notificationsService: NotificationsService;
}) => {
    if (userReminder) {
        await notificationsService.scheduleEventLocalNotification(
            createdEvent._raw,
        );
    }
};

const addFirebaseInvitations = ({
    eventContactsToAdd,
    firestoreService,
    organisationId,
    eventId,
}: {
    eventContactsToAdd: EventContactModel[];
    firestoreService: FirestoreService;
    organisationId: string;
    eventId: string;
}) => {
    for (let contact of eventContactsToAdd) {
        firestoreService.addDocumentToCollection('invitations', uuidv4(), {
            organisationId: organisationId,
            eventId: eventId,
            contactId: contact.contactId,
            status: EVENT_CONTACT_STATUS.AWAITING,
        });
    }
};

export {
    mapRawRecord,
    mapRecords,
    mapRawRecords,
    addRelatedDataBatch,
    prepareEventLocation,
    clearEventNotificationCache,
    schedulePN,
    addFirebaseInvitations,
};
